import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Modal } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { getPblCase, respondPblAppointmentOnBehalf, selectPblAppointmentProposal } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { Button } from '@/components/UI/Button';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblAppointmentOption, PblAppointmentProposal, PblCase } from '@/types/pbl';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import { TitleBar } from '../OcManageCases/styles';
import {
  ActionRow,
  BodyText,
  ConfirmPanel,
  ConfirmShell,
  FieldLabel,
  FieldValue,
  MeetingTitleRow,
  ModalActions,
  ModalSurface,
  ModalText,
  ModalTime,
  OptionLabel,
  OptionList,
  ReturnButton,
  SectionTitle,
  UnavailableReasonInput,
  UnavailableReasonLabel,
} from './styles';
import { useTranslation } from 'react-i18next';

const DEFAULT_MEETING_TITLE = 'Pro Bono Legal Consultation';
const UNAVAILABLE_SELECTION = 'unavailable';

type SelectionValue = number | typeof UNAVAILABLE_SELECTION | null;

const buildCaseDetailPath = (caseId: string | number) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const padTwoDigits = (value: number) => String(value).padStart(2, '0');

const parseDateTime = (value?: string | null) => {
  if (!value) return null;

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (date: Date) =>
  `${date.getFullYear()}/${padTwoDigits(date.getMonth() + 1)}/${padTwoDigits(date.getDate())}`;

const formatTime = (date: Date) => {
  const hour = date.getHours();
  const minute = date.getMinutes();
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;

  return `${padTwoDigits(hour12)}:${padTwoDigits(minute)} ${period}`;
};

const formatOptionTime = (option?: PblAppointmentOption | null) => {
  const startsAt = parseDateTime(option?.starts_at);
  const endsAt = parseDateTime(option?.ends_at);

  if (!startsAt) return '-';
  if (!endsAt) return `${formatDate(startsAt)} ${formatTime(startsAt)}`;

  const isSameDay =
    startsAt.getFullYear() === endsAt.getFullYear() &&
    startsAt.getMonth() === endsAt.getMonth() &&
    startsAt.getDate() === endsAt.getDate();

  return isSameDay
    ? `${formatDate(startsAt)} ${formatTime(startsAt)} - ${formatTime(endsAt)}`
    : `${formatDate(startsAt)} ${formatTime(startsAt)} - ${formatDate(endsAt)} ${formatTime(endsAt)}`;
};

const getMeetingTitle = () => DEFAULT_MEETING_TITLE;

const getLatestPendingProposal = (caseData?: PblCase | null) => {
  const latestProposal = caseData?.appointment_proposals?.[0];

  return latestProposal?.status === 'pending_citizen_selection' ? latestProposal : null;
};

const PblAppointmentProposalConfirm = ({ onBehalf = false }: { onBehalf?: boolean }) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResize();
  const [caseData, setCaseData] = useState<PblCase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selection, setSelection] = useState<SelectionValue>(null);
  const [unavailableReason, setUnavailableReason] = useState('');
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [proxyReason, setProxyReason] = useState('');
  const [consentConfirmed, setConsentConfirmed] = useState(false);

  const numericCaseId = Number(caseId);
  const pendingProposal = useMemo(() => getLatestPendingProposal(caseData), [caseData]);
  const selectedOption = typeof selection === 'number' ? pendingProposal?.options[selection] : null;
  const selectedTime = formatOptionTime(selectedOption);
  const caseDisplayId = caseData?.case_id || caseData?.case_reference || caseId || '-';
  const caseDetailPath = caseId ? buildCaseDetailPath(caseId) : PATH.pbl.citizenCases;

  useEffect(() => {
    if (!Number.isFinite(numericCaseId)) {
      errorHandler(t('pbl.pagesHardcoded.appointmentProposalConfirm.invalidCaseId'));
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    getPblCase(numericCaseId)
      .then((response) => {
        if (!isMounted) return;

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.appointmentProposalConfirm.failedToLoadCase'));
          return;
        }

        setCaseData(response.data.data);
      })
      .catch((error: any) => {
        if (isMounted) {
          errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.appointmentProposalConfirm.failedToLoadCase'));
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [numericCaseId]);

  const returnToCase = () => navigate(caseDetailPath);

  const submitUnavailable = async () => {
    const trimmedReason = unavailableReason.trim();

    if (!trimmedReason) {
      errorHandler(t('pbl.pagesHardcoded.appointmentProposalConfirm.pleaseEnterTheReasonWhyYouAreUnable'));
      return;
    }

    await submitResponse({ unavailable_reason: trimmedReason });
  };

  const submitSelectedOption = async () => {
    if (typeof selection !== 'number') return;

    await submitResponse({ selected_option_index: selection });
  };

  const submitResponse = async (payload: { selected_option_index: number } | { unavailable_reason: string }) => {
    if (!pendingProposal || submittingRef.current) return;
    if (onBehalf && (!proxyReason.trim() || !consentConfirmed)) {
      errorHandler(t('pbl.ocAppointmentProxy.required'));
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const response = onBehalf
        ? await respondPblAppointmentOnBehalf(pendingProposal.id, { ...payload, proxy_reason: proxyReason.trim(), citizen_consent_confirmed: true })
        : await selectPblAppointmentProposal(pendingProposal.id, payload);

      if (response.data.success !== true) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.appointmentProposalConfirm.failedToRespondToTheAppointmentProposal'));
        return;
      }

      successHandler(response.data.message || t('pbl.pagesHardcoded.appointmentProposalConfirm.appointmentProposalResponseSubmitted'));
      navigate(buildCaseDetailPath(pendingProposal.pbl_case_id || numericCaseId));
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.appointmentProposalConfirm.failedToRespondToTheAppointmentProposal')
      );
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      setAcceptOpen(false);
    }
  };

  const submitForm = (event: FormEvent) => {
    event.preventDefault();

    if (!pendingProposal) return;
    if (onBehalf && (!proxyReason.trim() || !consentConfirmed)) {
      errorHandler(t('pbl.ocAppointmentProxy.required'));
      return;
    }

    if (selection === null) {
      errorHandler(t('pbl.pagesHardcoded.appointmentProposalConfirm.pleaseSelectAMeetingTimeOrIndicateThat'));
      return;
    }

    if (selection === UNAVAILABLE_SELECTION) {
      submitUnavailable();
      return;
    }

    setAcceptOpen(true);
  };

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={returnToCase}>
            {t('pbl.pagesHardcoded.appointmentProposalConfirm.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.appointmentProposalConfirm.home')}{caseDisplayId} / <strong>{t('pbl.pagesHardcoded.appointmentProposalConfirm.confirmMeetingTime')}</strong>
          </span>
        </TitleBar>
      }
    >
      <ConfirmShell>
        <ConfirmPanel as="form" onSubmit={submitForm}>
          <SectionTitle>{t('pbl.pagesHardcoded.appointmentProposalConfirm.meeting')}</SectionTitle>
          {isLoading ? (
            <BodyText>{t('pbl.pagesHardcoded.appointmentProposalConfirm.loadingMeetingProposal')}</BodyText>
          ) : pendingProposal ? (
            <>
              {onBehalf && <>
                <BodyText>{t('pbl.ocAppointmentProxy.description')}</BodyText>
                <UnavailableReasonLabel>{t('pbl.ocAppointmentProxy.reason')}
                  <UnavailableReasonInput aria-label={t('pbl.ocAppointmentProxy.reason')} value={proxyReason}
                    maxLength={2000} disabled={isSubmitting} onChange={(event) => setProxyReason(event.target.value)} />
                </UnavailableReasonLabel>
                <OptionLabel><input type="checkbox" checked={consentConfirmed} disabled={isSubmitting}
                  onChange={(event) => setConsentConfirmed(event.target.checked)} />
                  <span>{t('pbl.ocAppointmentProxy.consent')}</span>
                </OptionLabel>
              </>}
              <BodyText>
                {t('pbl.pagesHardcoded.appointmentProposalConfirm.pleaseSelectAMeetingTimeThatYouCan')}</BodyText>
              <MeetingTitleRow>
                <FieldLabel>{t('pbl.pagesHardcoded.appointmentProposalConfirm.meetingTitle')}</FieldLabel>
                <FieldValue>{getMeetingTitle()}</FieldValue>
              </MeetingTitleRow>
              <OptionList>
                {pendingProposal.options.map((option, index) => {
                  const optionText = formatOptionTime(option);

                  return (
                    <OptionLabel key={`${pendingProposal.id}-${option.starts_at}-${index}`}>
                      <input
                        type="radio"
                        name="appointment-option"
                        checked={selection === index}
                        disabled={isSubmitting}
                        onChange={() => setSelection(index)}
                      />
                      <span>{optionText}</span>
                    </OptionLabel>
                  );
                })}
                <OptionLabel>
                  <input
                    type="radio"
                    name="appointment-option"
                    checked={selection === UNAVAILABLE_SELECTION}
                    disabled={isSubmitting}
                    onChange={() => setSelection(UNAVAILABLE_SELECTION)}
                  />
                  <span>{t('pbl.pagesHardcoded.appointmentProposalConfirm.participationIsNotPossibleDuringTheAboveTime')}</span>
                </OptionLabel>
              </OptionList>
              <UnavailableReasonLabel>
                {t('pbl.pagesHardcoded.appointmentProposalConfirm.pleaseEnterTheReasonWhyYouAreUnable2')}<UnavailableReasonInput
                  aria-label={t('pbl.pagesHardcoded.appointmentProposalConfirm.unavailableReason')}
                  value={unavailableReason}
                  disabled={isSubmitting}
                  onChange={(event) => setUnavailableReason(event.target.value)}
                />
              </UnavailableReasonLabel>
              <ActionRow>
                <Button type="submit" size="lg" variant="primary" rounded={false} disabled={isSubmitting}>
                  {isSubmitting ? t('pbl.pagesHardcoded.appointmentProposalConfirm.submitting') : t('pbl.pagesHardcoded.appointmentProposalConfirm.ok')}
                </Button>
                <ReturnButton type="button" onClick={returnToCase}>
                  {t('pbl.pagesHardcoded.appointmentProposalConfirm.returnToCase')}</ReturnButton>
              </ActionRow>
            </>
          ) : (
            <>
              <BodyText>{t('pbl.pagesHardcoded.appointmentProposalConfirm.noPendingMeetingProposalIsAvailable')}</BodyText>
              <ActionRow>
                <ReturnButton type="button" onClick={returnToCase}>
                  {t('pbl.pagesHardcoded.appointmentProposalConfirm.returnToCase')}</ReturnButton>
              </ActionRow>
            </>
          )}
        </ConfirmPanel>
      </ConfirmShell>

      <Modal open={acceptOpen} onClose={() => setAcceptOpen(false)} aria-labelledby="accept-meeting-time-title">
        <ModalSurface role="dialog" aria-modal="true" aria-label={t('pbl.pagesHardcoded.appointmentProposalConfirm.acceptMeetingTime')}>
          <ModalText id="accept-meeting-time-title">
            {t('pbl.pagesHardcoded.appointmentProposalConfirm.agreedToHoldAMeetingWithTheLawyer')}</ModalText>
          <ModalTime>{selectedTime}</ModalTime>
          <ModalActions>
            <Button
              type="button"
              size="lg"
              variant="primary"
              rounded={false}
              disabled={isSubmitting}
              onClick={submitSelectedOption}
            >
              {isSubmitting ? t('pbl.pagesHardcoded.appointmentProposalConfirm.submitting') : t('pbl.pagesHardcoded.appointmentProposalConfirm.accept')}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              disabled={isSubmitting}
              onClick={() => setAcceptOpen(false)}
            >
              {t('pbl.pagesHardcoded.appointmentProposalConfirm.cancel')}</Button>
          </ModalActions>
        </ModalSurface>
      </Modal>
    </PageContainer>
  );
};

export default PblAppointmentProposalConfirm;
